import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Api } from 'src/app/shared/constants/api';
import { KEYS } from 'src/app/shared/constants/keys';
import { DataService } from 'src/app/shared/services/data.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { IBranchContact } from '../external-entity.interface';
import { ExternalEntityService } from '../external-entity.service';
import { RowMobileState } from './branch-contact.interface';
import { Message } from 'src/app/shared/constants/message';

@Component({
    selector: 'app-branch-contact',
    templateUrl: './branch-contact.component.html',
    styleUrls: ['./branch-contact.component.less'],
    standalone: false
})
export class BranchContactComponent implements OnInit, OnDestroy {

  constructor(private formBuilder: FormBuilder,
              @Optional() private dailogRef: MatDialogRef<BranchContactComponent>,
              private logger: NGXLogger,
              private service: DataService,
              @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
              private utilityService: UtilityService,
              private entityService: ExternalEntityService) { }

  @Input() buttonHidden: boolean = false;
  @Input() skipFetch: boolean = false;
  @Input() isDialogMode: boolean = false;
  private _pendingData: any = null;
  private _disableTable: boolean;
  @Input() set disableTable(value: boolean) {
    this._disableTable = value;
    this.applyDisabledState();
  }
  get disableTable(): boolean { return this._disableTable; }

  @Input() set branchApiData(data: any) {
    if (!data) { return; }
    this._pendingData = data;
    if (this.branchContactDetailForm) { this._applyData(data); }
  }

  @Input() set sharedSegments(segs: any[]) {
    if (segs?.length) { this.businessSegments = segs; }
  }

  @Input() set sharedDialCodes(codes: { dialCode: string; countryName: string }[]) {
    if (!codes?.length) { return; }
    this.allDialCodes = codes;
    this.rowMobileState.forEach(state => { state.filteredDialCodes = [...codes]; });
  }

  private applyDisabledState() {
    if (!this.branchContactDetailForm) { return; }
    if (this._disableTable && !this.isDialogMode) {
      this.branchContactDetailForm.disable({ emitEvent: false });
    } else {
      this.branchContactDetailForm.enable({ emitEvent: false });
    }
  }

  @Input() set branchId(id: number) {
    this.id = id;
    if (this.branchContactDetailForm && !this.skipFetch) {
      this.getBranchById();
    }
  }

  id: number;
  branchContactList: IBranchContact[] = [];
  branchContactDetailForm: FormGroup;
  branchData: any;
  businessSegments: any[] = [];
  contactColumns = ['businessSegment', 'name', 'designation', 'email', 'cellNumber', 'isWaAlert', 'isEmailAlert', 'decisionMaker', 'gatekeeper', 'coach', 'veto', 'leftOrganization', 'action'];

  readonly MAX_WA_ALERT = 3;
  readonly MAX_EMAIL_ALERT = 5;

  get waAlertCount(): number {
    return this.contactDetailsDto.controls.filter(c => c.get('isWaAlert')?.value === true).length;
  }

  get emailAlertCount(): number {
    return this.contactDetailsDto.controls.filter(c => c.get('isEmailAlert')?.value === true).length;
  }

  /** True only when explicitly opened as a standalone dialog (showCloseButton passed via MAT_DIALOG_DATA). */
  get isOpenedAsDialog(): boolean { return !!this.data?.showCloseButton; }

  get displayedColumns(): string[] {
    return this.disableTable ? this.contactColumns.filter(c => c !== 'action') : this.contactColumns;
  }
  branchContactDataSource = new BehaviorSubject(this.branchContactList);

  allDialCodes: { dialCode: string; countryName: string }[] = [];
  rowMobileState: RowMobileState[] = [];
  private onDestroy$ = new Subject<void>();

  ngOnInit(): void {
    if (this.data) {
      if (typeof this.data === 'object' && !Array.isArray(this.data)) {
        if (!this.id) { this.id = this.data.branchId || this.data.id; }
        if (this.data.buttonHidden !== undefined) { this.buttonHidden = this.data.buttonHidden; }
        if (this.data.disableTable !== undefined) { this._disableTable = this.data.disableTable; }
        if (this.data.isDialogMode !== undefined) { this.isDialogMode = this.data.isDialogMode; }
      } else if (!this.id) {
        this.id = this.data;
      }
    }
    this.initializeForm();
    this.applyDisabledState();
    if (!this.skipFetch) {
      this.loadBusinessSegments();
      this.loadDialCodes();
    }
    if (this._pendingData) {
      this._applyData(this._pendingData);
    } else if (!this.skipFetch && this.id && !isNaN(this.id)) {
      this.getBranchById();
    }
  }

  private _applyData(data: any): void {
    this.branchData = data;
    this.branchContactList = data.contactDetails || data.contactDetailsDto || [];
    this.setContactForm();
    this.applyDisabledState();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.rowMobileState.forEach(state => {
      state.destroy$.next();
      state.destroy$.complete();
    });
  }

  initializeForm() {
    this.branchContactDetailForm = this.entityService.getContactDetailsForm();
  }

  private loadBusinessSegments() {
    const companyId = localStorage.getItem(KEYS.COMPANY_ID);
    if (companyId) {
      this.service.get(`${Api.COMPANY_BUSINESS_SEGMENT_DD}/${companyId}`).subscribe((res: any[]) => {
        if (res) { this.businessSegments = res; }
      });
    }
  }

  private loadDialCodes() {
    this.service.get(Api.EXTERNAL_ENTITY_DIAL_CODES).subscribe(res => {
      if (res) {
        this.allDialCodes = Object.entries(res).map(([dialCode, countryName]) => ({
          dialCode,
          countryName: countryName as string
        }));
        this.rowMobileState.forEach(state => {
          state.filteredDialCodes = [...this.allDialCodes];
        });
      }
    });
  }

  private createRowMobileState(existingCellNumber?: string): RowMobileState {
    const destroy$ = new Subject<void>();
    const dialCodeSearchCtrl = new FormControl();
    const phoneNumberCtrl = new FormControl('');
    const state: RowMobileState = {
      selectedDialCode: '',
      dialCodeSearchCtrl,
      phoneNumberCtrl,
      filteredDialCodes: [...this.allDialCodes],
      destroy$
    };

    if (existingCellNumber) {
      const match = existingCellNumber.match(/^(\+[\d-]{1,8})\s(.+)$/);
      if (match) {
        state.selectedDialCode = match[1];
        dialCodeSearchCtrl.setValue(match[1], { emitEvent: false });
        phoneNumberCtrl.setValue(match[2], { emitEvent: false });
      } else {
        phoneNumberCtrl.setValue(existingCellNumber, { emitEvent: false });
      }
    }

    dialCodeSearchCtrl.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntil(destroy$)
    ).subscribe(term => {
      if (!term || typeof term !== 'string') {
        state.filteredDialCodes = [...this.allDialCodes];
        return;
      }
      const lower = term.toLowerCase().trim();
      state.filteredDialCodes = this.allDialCodes.filter(
        item => item.countryName.toLowerCase().includes(lower) || item.dialCode.toLowerCase().includes(lower)
      );
    });

    return state;
  }

  setContactForm() {
    this.contactDetailsDto.clear();
    this.rowMobileState = [];
    this.branchContactList?.forEach(data => {
      this.contactDetailsDto.push(this.entityService.contactForm(data));
      this.rowMobileState.push(this.createRowMobileState(data?.cellNumber));
    });
    this.branchContactDataSource.next(this.contactDetailsDto.value);
  }

  addRow() {
    this.contactDetailsDto.push(this.entityService.contactForm());
    this.contactDetailsDto.markAsDirty();
    this.rowMobileState.push(this.createRowMobileState());
    this.branchContactDataSource.next(this.contactDetailsDto.value);
  }

  deleteRow(index: number) {
    this.contactDetailsDto.removeAt(index);
    this.contactDetailsDto.markAsDirty();
    const state = this.rowMobileState[index];
    if (state) {
      state.destroy$.next();
      state.destroy$.complete();
      this.rowMobileState.splice(index, 1);
    }
    this.branchContactDataSource.next(this.contactDetailsDto.value);
  }

  onSegmentChange(changedIndex: number): void {
    const selectedId = this.contactDetailsDto.at(changedIndex).get('businessSegmentId')?.value;
    const seg = this.businessSegments.find(s => s.businessSegmentId === selectedId);
    if (seg) {
      this.contactDetailsDto.at(changedIndex).get('businessSegmentName').setValue(seg.businessSegmentName);
    }
    this.contactDetailsDto.controls.forEach((row: any) => {
      const segId: number = row.get('businessSegmentId')?.value;
      const ctrl = row.get('businessSegmentId');
      if (!segId) {
        ctrl.setErrors(ctrl.touched ? { required: true } : null);
      } else {
        ctrl.setErrors(null);
      }
    });
  }

  getSegmentName(index: number): string {
    const id = this.contactDetailsDto.at(index).get('businessSegmentId')?.value;
    const seg = this.businessSegments.find(s => s.businessSegmentId === id);
    return seg ? seg.businessSegmentName : '';
  }

  validateContacts(): boolean {
    let valid = true;
    let mobileError = false;

    this.contactDetailsDto.controls.forEach((row: any) => {
      const segment: number = row.get('businessSegmentId')?.value;
      row.get('businessSegmentId').markAsTouched();
      if (!segment) {
        row.get('businessSegmentId').setErrors({ required: true });
        valid = false;
      } else {
        row.get('businessSegmentId').setErrors(null);
      }
      const cellNumber: string = row.get('cellNumber')?.value || '';
      if (cellNumber && cellNumber.length > 15) {
        row.get('cellNumber').setErrors({ maxlength: true });
        mobileError = true;
        valid = false;
      }
    });
    if (mobileError) {
      this.utilityService.notifier(Message.PHONE_MAX_15_DIGITS, 'error');
    } else if (!valid) {
      this.utilityService.notifier(Message.BRANCH_CONTACT_VALIDATION_ERROR, 'error');
    }
    return valid;
  }

  onRowCountrySelected(rowIndex: number, dialCode: string): void {
    const state = this.rowMobileState[rowIndex];
    if (!state) { return; }
    state.selectedDialCode = dialCode;
    state.dialCodeSearchCtrl.setValue(dialCode, { emitEvent: false });
    // Re-combine with existing number
    const numberValue = state.phoneNumberCtrl?.value || '';
    if (numberValue) {
      const cellCtrl = this.contactDetailsDto.at(rowIndex).get('cellNumber');
      cellCtrl?.setValue(`${dialCode} ${numberValue}`, { emitEvent: false });
      cellCtrl?.markAsDirty();
      this.branchContactDetailForm.markAsDirty();
    }
    setTimeout(() => {
      const phoneInput = document.getElementById(`phoneInput_${rowIndex}`) as HTMLInputElement;
      phoneInput?.focus();
    }, 0);
  }

  clearRowDialCode(rowIndex: number): void {
    const state = this.rowMobileState[rowIndex];
    if (!state) { return; }
    state.selectedDialCode = '';
    state.dialCodeSearchCtrl.setValue('', { emitEvent: false });
    state.filteredDialCodes = [...this.allDialCodes];
    // Store just the number without dial code
    const numberValue = state.phoneNumberCtrl?.value || '';
    const cellCtrl = this.contactDetailsDto.at(rowIndex).get('cellNumber');
    cellCtrl?.setValue(numberValue || null, { emitEvent: false });
    cellCtrl?.markAsDirty();
    this.branchContactDetailForm.markAsDirty();
  }

  onPhoneInput(rowIndex: number, event: Event): void {
    const numberValue = (event.target as HTMLInputElement).value;
    const state = this.rowMobileState[rowIndex];
    const cellCtrl = this.contactDetailsDto.at(rowIndex).get('cellNumber');
    const combined = (state?.selectedDialCode && numberValue)
      ? `${state.selectedDialCode} ${numberValue}`
      : (numberValue || null);
    cellCtrl?.setValue(combined, { emitEvent: false });
    cellCtrl?.markAsDirty();
    this.branchContactDetailForm.markAsDirty();
  }

  onPhoneKeyDown(rowIndex: number, event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (allowedKeys.includes(event.key)) { return; }
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      return;
    }
    const state = this.rowMobileState[rowIndex];
    if (!state?.selectedDialCode) {
      event.preventDefault();
      this.utilityService.notifier(Message.SELECT_COUNTRY_CODE_FIRST, 'error');
      return;
    }
    const input = event.target as HTMLInputElement;
    const dialDigits = (state?.selectedDialCode || '').replace(/[^\d]/g, '').length;
    const currentDigits = (input.value || '').replace(/[^\d]/g, '').length;
    if (dialDigits + currentDigits >= 15) {
      event.preventDefault();
      this.utilityService.notifier(Message.PHONE_MAX_15_DIGITS, 'warning');
    }
  }

  onSave() {
    this.branchContactDetailForm.markAllAsTouched();
    if (!this.validateContacts()) { return; }
    const contactDetails = this.contactDetailsDto.value.map(element => ({
      ...element,
      entityBranchId: this.id
    }));
    this.service.post(`${Api.ENTITY_BRANCH}/create-branch-contacts?branchId=${this.id}`, contactDetails).subscribe(() => {
      this.utilityService.notifier(Message.BRANCH_CONTACT_SAVED, 'success');
      this.dailogRef?.close();
    });
  }

  trimControl(ctrl: AbstractControl) {
    if (ctrl?.value != null) {
      ctrl.setValue(ctrl.value.trim(), { emitEvent: false });
      ctrl.updateValueAndValidity();
    }
  }

  onCancel() {
    this.dailogRef?.close();
  }

  getBranchById() {
    if (this.id) {
      this.service.get(Api.ENTITY_BRANCH + '/' + this.id).subscribe(res => {
        this.branchData = res;
        this.branchContactList = res.contactDetails || res.contactDetailsDto || [];
        this.setContactForm();
        this.applyDisabledState();
      });
    }
  }

  getDialCodeTooltip(rowIndex: number): string {
    const dialCode = this.rowMobileState[rowIndex]?.selectedDialCode;
    if (!dialCode) { return ''; }
    const item = this.allDialCodes.find(d => d.dialCode === dialCode);
    return item ? `${item.countryName} (${item.dialCode})` : dialCode;
  }

  get contactDetailsDto() { return (<FormArray>this.branchContactDetailForm.get('contactDetailsDto')); }
}
